import React, { useMemo, useState } from "react";
import { Box, CircularProgress, InputAdornment, Stack, TextField, Typography } from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import LockRoundedIcon   from "@mui/icons-material/LockRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import FormTreeNav from "../FormTreeNav";

/**
 * 좌측 양식 목록.
 *
 * 양식이 7종일 때는 카드 그리드가 한눈에 들어왔지만 19종이 되면서 스크롤이 길어졌다.
 * 그래서 사이드바 + 검색 + 폴더로 바꿨다 — 목록을 계속 보면서 오른쪽에서 작성할 수 있다.
 *
 * 폴더 묶는 규칙은 {@link ../formTree} 에 있고 관리자 화면과 같은 것을 쓴다.
 *
 * @param lockedFormName 있으면 «양식 고정» 모드 — 목록 대신 이 이름 하나와 잠근 이유만 보인다.
 *                       수정 모달이 넘긴다. 신규 상신은 넘기지 않으므로 동작이 그대로다.
 */
export default function FormSidebar({ groups, loading, selectedCode, onSelect, lockedFormName }) {
    const [q, setQ] = useState("");

    const filtered = useMemo(() => {
        const kw = q.trim().toLowerCase();
        if (!kw) return groups;
        return (groups || [])
            .map((g) => ({ ...g, forms: g.forms.filter((f) => f.name.toLowerCase().includes(kw)) }))
            .filter((g) => g.forms.length > 0);
    }, [groups, q]);

    /* 양식 고정(수정 모드) — 목록·검색을 통째로 걷어내고 현재 양식 하나만 남긴다.
       «비활성 목록 + 마우스 올리면 뜨는 안내» 도 검토했지만 버렸다. 19줄이 회색으로 그대로 남으면
       눌러도 반응이 없는 화면이 되어 «고장» 으로 읽히고, 이유는 커서를 정확히 올린 사람만 본다.
       고를 수 없는 것을 보여주지 않는 편이 «왜» 를 항상 보이게 하는 유일한 배치였다. */
    if (lockedFormName) {
        return (
            <Box sx={{
                width: 280, flexShrink: 0, borderRight: "1px solid #e2e8f0",
                display: "flex", flexDirection: "column", bgcolor: "#f8fafc",
            }}>
                <Stack direction="row" alignItems="center" spacing={0.75}
                       sx={{ p: 1.5, borderBottom: "1px solid #e2e8f0" }}>
                    <LockRoundedIcon sx={{ fontSize: 15, color: "#94a3b8" }} />
                    <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: "#334155" }}>
                        수정 중에는 양식을 바꿀 수 없습니다
                    </Typography>
                </Stack>

                <Box sx={{ p: 1.5 }}>
                    <Stack direction="row" alignItems="flex-start" spacing={0.75}
                           sx={{ p: 1.25, borderRadius: 1.5, bgcolor: "#fff",
                                 border: "1px solid #e2e8f0" }}>
                        <DescriptionRoundedIcon sx={{ fontSize: 16, color: "#2563eb", mt: 0.15, flexShrink: 0 }} />
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a", lineHeight: 1.4 }}>
                            {lockedFormName}
                        </Typography>
                    </Stack>

                    {/* 대안을 같이 적는다 — «못 한다» 만 있으면 화면을 닫고 다시 시도하게 된다. */}
                    <Typography sx={{ fontSize: 11.5, color: "#64748b", mt: 1.25, lineHeight: 1.6 }}>
                        양식이 달라지면 입력칸 구성이 통째로 바뀌어 이미 저장된 값이 갈 곳을 잃습니다.
                        다른 양식으로 올리려면 이 문서를 회수하거나 삭제한 뒤 새로 상신해 주세요.
                    </Typography>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{
            width: 280, flexShrink: 0, borderRight: "1px solid #e2e8f0",
            display: "flex", flexDirection: "column", bgcolor: "#f8fafc",
        }}>
            <Box sx={{ p: 1.5, borderBottom: "1px solid #e2e8f0" }}>
                <TextField
                    size="small" fullWidth placeholder="양식 검색"
                    value={q} onChange={(e) => setQ(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchRoundedIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                            </InputAdornment>
                        ),
                        sx: { bgcolor: "#fff", fontSize: 13 },
                    }}
                />
            </Box>

            <Box sx={{ flex: 1, overflowY: "auto", py: 1 }}>
                {loading && (
                    <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress size={22} /></Stack>
                )}

                {!loading && filtered.length === 0 && (
                    <Typography sx={{ px: 2, py: 3, fontSize: 12.5, color: "#94a3b8", textAlign: "center" }}>
                        {q ? "검색 결과가 없습니다." : "상신할 수 있는 양식이 없습니다."}
                    </Typography>
                )}

                <FormTreeNav
                    groups={filtered}
                    keyOf={(f) => f.code}
                    selected={selectedCode}
                    onSelect={onSelect}
                    query={q}
                />
            </Box>
        </Box>
    );
}
